import { withSelectedBundle } from '../utils/selectedAcademicYear';
import { mapYearLockedError } from '../utils/classAcademicYear';
import { supabase } from '../supabaseClient';
import { publishTodoNoteWorkspaceSnapshot, setTodoNoteWorkspaceQueryData } from '../query/noteCacheUpdates';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft } from '../types/todoNote';
import {
  loadTodoNoteShareBridge,
  saveTodoNoteShareBridge,
} from '../utils/todoNoteShareBridgeCache';
import {
  loadTodoNoteWorkspaceCache,
  saveTodoNoteWorkspaceCache,
  type TodoNoteWorkspaceSnapshot,
} from '../utils/todoNoteWorkspaceCache';
import {
  buildTodoShareSavePayload,
  listTodoShareBoards,
  loadTodoShareAsOwner,
  migratePersonalTodoToDefaultBoard,
  saveTodoShareAsOwner,
  TodoShareRevisionConflictError,
} from './todoShareService';
import { defaultRoutineConfig } from '../utils/todoRoutine';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';

export type { TodoNoteWorkspaceSnapshot };

function nowIso(): string {
  return new Date().toISOString();
}

function applyWorkspaceScopeFilter<T extends { eq: (c: string, v: string) => T; is: (c: string, v: null) => T }>(
  q: T,
  noteWorkspaceId: string | null
): T {
  if (noteWorkspaceId === null) return q.is('workspace_id', null);
  return q.eq('workspace_id', noteWorkspaceId);
}

function snapshotFromShare(
  categories: TodoNoteCategoryDraft[],
  items: TodoNoteItemDraft[],
  noteWorkspaceId: string | null
): TodoNoteWorkspaceSnapshot {
  return {
    categories: categories.map((c) => ({
      ...c,
      workspace_id: c.workspace_id ?? noteWorkspaceId,
      note_memo: typeof c.note_memo === 'string' ? c.note_memo : '',
    })),
    items: items.map((it) => ({
      ...it,
      workspace_id: it.workspace_id ?? noteWorkspaceId,
    })),
  };
}

async function fetchTodoNoteWorkspaceFromDb(
  uid: string,
  noteWorkspaceId: string | null
): Promise<TodoNoteWorkspaceSnapshot> {
  const catBase = supabase
    .from('todo_note_categories')
    .select('id,user_id,workspace_id,name,color,sort_order,note_memo,created_at,updated_at')
    .eq('user_id', uid)
    .order('sort_order', { ascending: true });
  const itemBase = supabase
    .from('todo_note_items')
    .select(
      'id,user_id,workspace_id,category_id,due_kind,due_date,title,body,misc,completed,bucket_order,is_routine,routine,is_favorite,created_at,updated_at'
    )
    .match(withSelectedBundle({ user_id: uid }));

  const [{ data: cats, error: cErr }, { data: its, error: iErr }] = await Promise.all([
    applyWorkspaceScopeFilter(catBase, noteWorkspaceId),
    applyWorkspaceScopeFilter(itemBase, noteWorkspaceId),
  ]);

  if (cErr) throw cErr;
  if (iErr) throw iErr;

  const snapshot: TodoNoteWorkspaceSnapshot = {
    categories: ((cats ?? []) as TodoNoteCategoryDraft[]).map((c) => ({
      ...c,
      workspace_id: c.workspace_id ?? noteWorkspaceId,
      note_memo: typeof c.note_memo === 'string' ? c.note_memo : '',
    })),
    items: ((its ?? []) as TodoNoteItemDraft[]).map((it) => ({
      ...it,
      workspace_id: it.workspace_id ?? noteWorkspaceId,
      is_routine: Boolean(it.is_routine),
      routine: it.is_routine && it.routine ? defaultRoutineConfig(it.routine as never) : null,
      is_favorite: Boolean(it.is_favorite),
    })),
  };
  saveTodoNoteWorkspaceCache(uid, noteWorkspaceId, snapshot);
  setTodoNoteWorkspaceQueryData(uid, noteWorkspaceId, snapshot);
  return snapshot;
}

/** 기본 공유 보드 스냅샷 로드. 성공 시 bridge+캐시 갱신. 실패 시 null. */
async function fetchFromDefaultShareBoard(
  uid: string,
  noteWorkspaceId: string | null
): Promise<TodoNoteWorkspaceSnapshot | null> {
  const publishSnap = (
    boardId: string,
    revision: number,
    categories: TodoNoteCategoryDraft[],
    items: TodoNoteItemDraft[]
  ) => {
    const snapshot = snapshotFromShare(categories, items, noteWorkspaceId);
    publishTodoNoteWorkspaceSnapshot(uid, noteWorkspaceId, snapshot, { boardId, revision });
    return snapshot;
  };

  try {
    const bridge = loadTodoNoteShareBridge(uid, noteWorkspaceId);
    if (bridge?.boardId) {
      try {
        const snap = await loadTodoShareAsOwner(bridge.boardId);
        return publishSnap(bridge.boardId, snap.revision, snap.categories, snap.items);
      } catch {
        // stale bridge → migrate로 재해석
      }
    }

    const board = await migratePersonalTodoToDefaultBoard(noteWorkspaceId);
    const snap = await loadTodoShareAsOwner(board.id);
    return publishSnap(board.id, snap.revision, snap.categories, snap.items);
  } catch {
    return null;
  }
}

/** 기본 공유 보드 revision head와 LS bridge를 비교. 동일하면 풀 스냅샷 fetch 생략 가능. */
async function isTodoNoteWorkspaceCacheFresh(
  uid: string,
  noteWorkspaceId: string | null
): Promise<boolean> {
  const bridge = loadTodoNoteShareBridge(uid, noteWorkspaceId);
  if (!bridge) return false;
  try {
    const boards = await listTodoShareBoards(noteWorkspaceId);
    const board = boards.find((b) => b.id === bridge.boardId);
    if (!board) return false;
    return Number(board.revision) === Number(bridge.revision);
  } catch {
    return false;
  }
}

export async function fetchTodoNoteWorkspace(
  noteWorkspaceId: string | null,
  options?: { forceRefresh?: boolean }
): Promise<TodoNoteWorkspaceSnapshot> {
  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
  if (sessionErr) throw sessionErr;
  const uid = sessionData.session?.user?.id;
  if (!uid) throw new Error('로그인이 필요합니다.');

  if (!options?.forceRefresh) {
    const cached = loadTodoNoteWorkspaceCache(uid, noteWorkspaceId);
    if (cached.found) {
      const fresh = await isTodoNoteWorkspaceCacheFresh(uid, noteWorkspaceId);
      if (fresh) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('Todo노트·LS(revision head 일치)');
        }
        return cached.data;
      }
    }
  }

  const fromShare = await fetchFromDefaultShareBoard(uid, noteWorkspaceId);
  if (fromShare) return fromShare;

  return fetchTodoNoteWorkspaceFromDb(uid, noteWorkspaceId);
}

export interface SaveTodoNoteWorkspaceParams {
  noteWorkspaceId: string | null;
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds: string[];
}

async function resolveDefaultShareBoardForSave(
  uid: string,
  noteWorkspaceId: string | null
): Promise<{ boardId: string; revision: number } | null> {
  const bridge = loadTodoNoteShareBridge(uid, noteWorkspaceId);
  if (bridge) return bridge;
  try {
    const board = await migratePersonalTodoToDefaultBoard(noteWorkspaceId);
    saveTodoNoteShareBridge(uid, noteWorkspaceId, {
      boardId: board.id,
      revision: board.revision,
    });
    return { boardId: board.id, revision: board.revision };
  } catch {
    return null;
  }
}

/** revision conflict 시: 서버 스냅샷 위에 로컬 upsert를 병합(서버에만 있는 항목 보존) */
function mergeShareSnapshotWithLocalSave(
  fresh: { categories: TodoNoteCategoryDraft[]; items: TodoNoteItemDraft[] },
  local: TodoNoteWorkspaceSnapshot,
  deletedCategoryIds: string[],
  deletedItemIds: string[]
): TodoNoteWorkspaceSnapshot {
  const deletedCat = new Set(deletedCategoryIds);
  const deletedItem = new Set(deletedItemIds);
  const catById = new Map<string, TodoNoteCategoryDraft>();
  for (const c of fresh.categories) {
    if (!deletedCat.has(c.id)) catById.set(c.id, c);
  }
  for (const c of local.categories) {
    if (!deletedCat.has(c.id)) catById.set(c.id, { ...catById.get(c.id), ...c });
  }
  const itemById = new Map<string, TodoNoteItemDraft>();
  for (const it of fresh.items) {
    if (!deletedItem.has(it.id)) itemById.set(it.id, it);
  }
  for (const it of local.items) {
    if (!deletedItem.has(it.id)) itemById.set(it.id, { ...itemById.get(it.id), ...it });
  }
  return {
    categories: Array.from(catById.values()),
    items: Array.from(itemById.values()),
  };
}

async function saveTodoNoteWorkspaceToLegacy(
  uid: string,
  params: SaveTodoNoteWorkspaceParams
): Promise<void> {
  const ws = params.noteWorkspaceId;
  const ts = nowIso();
  const catsPayload = params.categories.map((c) => ({
    id: c.id,
    user_id: uid,
    workspace_id: ws,
    name: c.name.trim(),
    color: c.color,
    sort_order: c.sort_order,
    note_memo: c.note_memo ?? '',
    updated_at: ts,
  }));

  const itemsPayload = params.items.map((r) => ({
    id: r.id,
    user_id: uid,
    workspace_id: ws,
    category_id: r.category_id,
    due_kind: r.due_kind,
    due_date: r.due_date,
    title: r.title,
    body: r.body,
    misc: r.misc,
    completed: r.completed,
    bucket_order: r.bucket_order,
    is_routine: Boolean(r.is_routine),
    routine: r.is_routine && r.routine ? defaultRoutineConfig(r.routine) : null,
    is_favorite: Boolean(r.is_favorite),
    updated_at: ts,
  }));

  if (catsPayload.length > 0) {
    const { error } = await supabase.from('todo_note_categories').upsert(catsPayload, { onConflict: 'id' });
    if (error) throw error;
  }

  if (itemsPayload.length > 0) {
    const { error } = await supabase.from('todo_note_items').upsert(itemsPayload, { onConflict: 'id' });
    if (error) throw mapYearLockedError(error);
  }

  if (params.deletedItemIds.length > 0) {
    const { error } = await supabase.from('todo_note_items').delete().in('id', params.deletedItemIds);
    if (error) throw error;
  }

  if (params.deletedCategoryIds.length > 0) {
    const { error } = await supabase.from('todo_note_categories').delete().in('id', params.deletedCategoryIds);
    if (error) throw error;
  }

  const deletedCategoryIdSet = new Set(params.deletedCategoryIds);
  const deletedItemIdSet = new Set(params.deletedItemIds);
  const snapshot: TodoNoteWorkspaceSnapshot = {
    categories: params.categories.filter((c) => !deletedCategoryIdSet.has(c.id)),
    items: params.items.filter((it) => !deletedItemIdSet.has(it.id)),
  };
  publishTodoNoteWorkspaceSnapshot(uid, ws, snapshot);
}

export async function saveTodoNoteWorkspace(params: SaveTodoNoteWorkspaceParams): Promise<void> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr) throw userErr;
  const uid = userData.user?.id;
  if (!uid) throw new Error('로그인이 필요합니다.');

  const ws = params.noteWorkspaceId;
  const deletedCategoryIdSet = new Set(params.deletedCategoryIds);
  const deletedItemIdSet = new Set(params.deletedItemIds);
  const snapshot: TodoNoteWorkspaceSnapshot = {
    categories: params.categories.filter((c) => !deletedCategoryIdSet.has(c.id)),
    items: params.items.filter((it) => !deletedItemIdSet.has(it.id)),
  };

  const target = await resolveDefaultShareBoardForSave(uid, ws);
  if (target) {
    try {
      const newRev = await saveTodoShareAsOwner({
        boardId: target.boardId,
        expectedRevision: target.revision,
        payload: buildTodoShareSavePayload({
          categories: snapshot.categories,
          items: snapshot.items,
          deletedCategoryIds: params.deletedCategoryIds,
          deletedItemIds: params.deletedItemIds,
        }),
      });
      publishTodoNoteWorkspaceSnapshot(uid, ws, snapshot, {
        boardId: target.boardId,
        revision: newRev,
      });
      return;
    } catch (e) {
      if (e instanceof TodoShareRevisionConflictError) {
        // 최신 서버 상태와 병합 후 1회 재시도 (서버 전용 항목 보존)
        const fresh = await loadTodoShareAsOwner(target.boardId);
        const merged = mergeShareSnapshotWithLocalSave(
          fresh,
          snapshot,
          params.deletedCategoryIds,
          params.deletedItemIds
        );
        const newRev = await saveTodoShareAsOwner({
          boardId: target.boardId,
          expectedRevision: fresh.revision,
          payload: buildTodoShareSavePayload({
            categories: merged.categories,
            items: merged.items,
            deletedCategoryIds: params.deletedCategoryIds,
            deletedItemIds: params.deletedItemIds,
          }),
        });
        publishTodoNoteWorkspaceSnapshot(uid, ws, merged, {
          boardId: target.boardId,
          revision: newRev,
        });
        return;
      }
      // 공유 보드 저장 실패 시 레거시로 폴백
    }
  }

  await saveTodoNoteWorkspaceToLegacy(uid, params);
}
